import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/Button';
import { MarketRow, TabId } from './ListMarkets.types';
import { useMarkets } from 'apiTyped/hooks/v2.markets.query';
import { useV2Filters } from '../context/V2FiltersContext';

// ─── Component ───────────────────────────────────────────────────────────────

export const ListMarkets: React.FC = () => {
  const { filters } = useV2Filters();
  const { isLoading, markets } = useMarkets({
    channel: filters.channel,
    brand: filters.brand,
    productType: filters.productType
  });
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [selectedMarket, setSelectedMarket] = useState<string>('no');

  if (isLoading && markets.length === 0) {
    return (
      <Page>
        <PageHeader title="List Markets" subtitle="Loading markets data…" />
        <LoadingWrap>Loading…</LoadingWrap>
      </Page>
    );
  }

  const totalSurveyed = markets.reduce((s, m) => s + m.accountsSurveyed, 0);
  const totalDn = markets.reduce((s, m) => s + m.dnLastYear, 0);
  const totalGained = markets.reduce((s, m) => s + m.gained, 0);
  const totalLost = markets.reduce((s, m) => s + m.lost, 0);
  const totalNet = totalGained - totalLost;

  const selected = markets.find((m) => m.id === selectedMarket) || markets[0];

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="List Markets"
        subtitle="Cross-market synthesis of distribution health: which markets are growing, which are slipping, and how each is being surveyed."
      >
        <Button icon={<ExportIcon />}>Export synthesis</Button>
      </PageHeader>

      {/* Tabs */}
      <TabBar
        tabs={[
          {
            id: 'overview',
            label: 'List Markets',
            icon: (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 3v18h18" />
                <rect x="7" y="13" width="3" height="5" />
                <rect x="13" y="9" width="3" height="9" />
                <rect x="19" y="5" width="3" height="13" />
              </svg>
            )
          },
          {
            id: 'detail',
            label: 'Detail by Market',
            icon: (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            )
          }
        ]}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        meta={
          <>
            {markets.length} markets surveyed · {totalSurveyed.toLocaleString()}{' '}
            accounts · Last sync 18 May 2026
          </>
        }
      />

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <>
          <KpiStrip
            items={[
              {
                label: 'Total active POS · brand',
                value: totalSurveyed.toLocaleString(),
                delta: {
                  value: `+${totalNet} vs. last quarter`,
                  direction: 'up'
                }
              },
              {
                label: 'DN baseline · last year',
                value: totalDn.toLocaleString(),
                delta: { value: 'Reference baseline', direction: 'flat' }
              },
              {
                label: 'Net movement',
                value: `+${totalNet}`,
                delta: {
                  value: `+${totalGained} gained · −${totalLost} lost`,
                  direction: 'up'
                }
              },
              {
                label: 'Penetration · cross-market avg',
                value: '26.4%',
                delta: { value: '+2.1 pt vs. last quarter', direction: 'up' }
              }
            ]}
          />

          {/* Markets Table */}
          <TableSection>
            <TableHeader>
              <div>
                <CardTitle>Markets snapshot</CardTitle>
                <CardMeta>
                  All KPIs from the survey panel — click any row to drill into
                  Detail by Market
                </CardMeta>
              </div>
            </TableHeader>
            <TableWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>
                      # Accounts surveyed<ColSub>by WS</ColSub>
                    </th>
                    <th>
                      DN<ColSub>last year</ColSub>
                    </th>
                    <th>
                      Gained<ColSub>period</ColSub>
                    </th>
                    <th>
                      Lost<ColSub>period</ColSub>
                    </th>
                    <th>
                      Not surveyed<ColSub>churned</ColSub>
                    </th>
                    <th>
                      Newly surveyed<ColSub>period</ColSub>
                    </th>
                    <th>Net</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((market) => (
                    <TableRow
                      key={market.id}
                      onClick={() => {
                        setSelectedMarket(market.id);
                        setActiveTab('detail');
                      }}
                    >
                      <td>
                        <MarketCell>
                          <MarketFlag>{market.flag}</MarketFlag>
                          <MarketName>
                            {market.name}
                            <MarketSub>
                              {market.tier} · {market.cities} cities
                            </MarketSub>
                          </MarketName>
                        </MarketCell>
                      </td>
                      <td>{market.accountsSurveyed.toLocaleString()}</td>
                      <td>{market.dnLastYear.toLocaleString()}</td>
                      <CellPos $strong={market.gained > 50}>
                        +{market.gained}
                      </CellPos>
                      <CellNeg $strong={market.lost > 50}>
                        −{market.lost}
                      </CellNeg>
                      <CellNeutral>{market.notSurveyed}</CellNeutral>
                      <td>{market.newlySurveyed}</td>
                      <td>
                        <CellBarRow>
                          <CellBar>
                            <CellBarInner
                              $width={Math.min(
                                (Math.abs(market.net) / 120) * 100,
                                100
                              )}
                              $positive={market.net >= 0}
                            />
                          </CellBar>
                          {market.net >= 0 ? (
                            <CellPos $strong>+{market.net}</CellPos>
                          ) : (
                            <CellNeg $strong>{market.net}</CellNeg>
                          )}
                        </CellBarRow>
                      </td>
                      <ChevCell>›</ChevCell>
                    </TableRow>
                  ))}
                </tbody>
              </StyledTable>
            </TableWrap>
          </TableSection>

          {/* Charts row */}
          <SplitRow>
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Surveyed vs. DN — by market</CardTitle>
                  <CardMeta>
                    Total surveyed accounts compared against DN baseline
                  </CardMeta>
                </div>
                <Legend>
                  <LegendItem>
                    <LegendDot $color="var(--olive)" />
                    Surveyed
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $color="var(--terracotta)" />
                    DN last year
                  </LegendItem>
                </Legend>
              </CardHeader>
              <ChartArea>
                <BarChart>
                  {markets.slice(0, 8).map((m) => {
                    const maxVal = 2200;
                    const surveyedHeight = (m.accountsSurveyed / maxVal) * 100;
                    const dnHeight = (m.dnLastYear / maxVal) * 100;
                    return (
                      <BarGroup key={m.id}>
                        <Bar
                          $height={dnHeight}
                          $color="var(--terracotta)"
                          title={`DN: ${m.dnLastYear}`}
                        />
                        <Bar
                          $height={surveyedHeight}
                          $color="var(--olive)"
                          title={`Surveyed: ${m.accountsSurveyed}`}
                        />
                        <BarLabel>{m.id.toUpperCase()}</BarLabel>
                      </BarGroup>
                    );
                  })}
                </BarChart>
              </ChartArea>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <CardTitle>CPR Evolution</CardTitle>
                  <CardMeta>
                    Consumption Penetration Rate — 12 months · global
                  </CardMeta>
                </div>
              </CardHeader>
              <CprGrid>
                <CprTile>
                  <CprTileLabel>Current CPR</CprTileLabel>
                  <CprTileValue>
                    18.4<CprUnit>%</CprUnit>
                  </CprTileValue>
                  <CprTrend $up>↑ +2.1pt YoY</CprTrend>
                </CprTile>
                <CprTile>
                  <CprTileLabel>12m High</CprTileLabel>
                  <CprTileValue>
                    19.2<CprUnit>%</CprUnit>
                  </CprTileValue>
                  <CprTrend>Mar 2026</CprTrend>
                </CprTile>
                <CprTile>
                  <CprTileLabel>12m Low</CprTileLabel>
                  <CprTileValue>
                    15.3<CprUnit>%</CprUnit>
                  </CprTileValue>
                  <CprTrend>Jul 2025</CprTrend>
                </CprTile>
              </CprGrid>
              <ChartArea>
                <svg
                  viewBox="0 0 520 180"
                  preserveAspectRatio="none"
                  style={{ width: '100%', height: 160 }}
                >
                  <defs>
                    <linearGradient
                      id="cprAreaGrad"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#c84b1f"
                        stopOpacity={0.18}
                      />
                      <stop offset="100%" stopColor="#c84b1f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <g stroke="#ede7db" strokeWidth="1">
                    <line x1="0" y1="30" x2="520" y2="30" />
                    <line x1="0" y1="80" x2="520" y2="80" />
                    <line x1="0" y1="130" x2="520" y2="130" />
                  </g>
                  <path
                    d="M30,100 L80,115 L130,120 L180,108 L230,100 L280,90 L330,80 L380,66 L430,54 L480,48 L510,40 L510,160 L30,160 Z"
                    fill="url(#cprAreaGrad)"
                  />
                  <path
                    d="M30,100 L80,115 L130,120 L180,108 L230,100 L280,90 L330,80 L380,66 L430,54 L480,48 L510,40"
                    stroke="#c84b1f"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="510" cy="40" r="4" fill="#c84b1f" />
                  <circle cx="510" cy="40" r="8" fill="#c84b1f" opacity={0.2} />
                </svg>
              </ChartArea>
            </Card>
          </SplitRow>
        </>
      )}

      {/* TAB 2: DETAIL BY MARKET */}
      {activeTab === 'detail' && (
        <>
          {/* Market selector */}
          <MarketSelector>
            <MarketSelectorLabel>Market</MarketSelectorLabel>
            {markets.map((m) => (
              <MarketChip
                key={m.id}
                $active={selectedMarket === m.id}
                onClick={() => setSelectedMarket(m.id)}
              >
                <span>{m.flag}</span> {m.name}
              </MarketChip>
            ))}
          </MarketSelector>

          {/* Detail hero */}
          <DetailHero>
            <DetailFlag>{selected.flag}</DetailFlag>
            <DetailInfo>
              <DetailName>{selected.name}</DetailName>
              <DetailSub>
                {selected.tier} market · {selected.cities} cities ·{' '}
                {selected.accountsSurveyed} POS surveyed
              </DetailSub>
            </DetailInfo>
            <DetailStats>
              <DetailStat>
                <DetailStatLabel>Score</DetailStatLabel>
                <DetailStatValue>
                  74<DetailStatUnit>/100</DetailStatUnit>
                </DetailStatValue>
              </DetailStat>
              <DetailStat>
                <DetailStatLabel>CPR</DetailStatLabel>
                <DetailStatValue>
                  21.4<DetailStatUnit>%</DetailStatUnit>
                </DetailStatValue>
              </DetailStat>
              <DetailStat>
                <DetailStatLabel>Net Movement</DetailStatLabel>
                <DetailStatValue $positive={selected.net >= 0}>
                  {selected.net >= 0 ? `+${selected.net}` : selected.net}
                </DetailStatValue>
              </DetailStat>
            </DetailStats>
          </DetailHero>

          {/* 6 KPI tiles */}
          <DetailGrid>
            <DetailTile>
              <DetailTileLabel>Accounts surveyed by WS</DetailTileLabel>
              <DetailTileValue>{selected.accountsSurveyed}</DetailTileValue>
              <DetailTileFoot>
                <DeltaUp>
                  ↑ +{selected.accountsSurveyed - selected.dnLastYear}
                </DeltaUp>{' '}
                vs last year
              </DetailTileFoot>
            </DetailTile>
            <DetailTile>
              <DetailTileLabel>DN last year (baseline)</DetailTileLabel>
              <DetailTileValue>{selected.dnLastYear}</DetailTileValue>
              <DetailTileFoot>Reference for movement</DetailTileFoot>
            </DetailTile>
            <DetailTile>
              <DetailTileLabel>POS Gained</DetailTileLabel>
              <DetailTileValue $color="var(--olive)">
                +{selected.gained}
              </DetailTileValue>
              <DetailTileFoot>
                <DeltaUp>↑ +18</DeltaUp> vs prior period
              </DetailTileFoot>
            </DetailTile>
            <DetailTile>
              <DetailTileLabel>POS Lost</DetailTileLabel>
              <DetailTileValue $color="var(--crimson)">
                −{selected.lost}
              </DetailTileValue>
              <DetailTileFoot>
                <DeltaDown>↓ +6</DeltaDown> vs prior period
              </DetailTileFoot>
            </DetailTile>
            <DetailTile>
              <DetailTileLabel>Not surveyed (churned)</DetailTileLabel>
              <DetailTileValue>{selected.notSurveyed}</DetailTileValue>
              <DetailTileFoot>POS not reached this period</DetailTileFoot>
            </DetailTile>
            <DetailTile>
              <DetailTileLabel>Newly surveyed</DetailTileLabel>
              <DetailTileValue>{selected.newlySurveyed}</DetailTileValue>
              <DetailTileFoot>
                <DeltaUp>↑ +8</DeltaUp> vs prior period
              </DetailTileFoot>
            </DetailTile>
          </DetailGrid>

          {/* Charts: Movement + CPR */}
          <SplitRow>
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>
                    {selected.name} — POS Movement (12 months)
                  </CardTitle>
                  <CardMeta>Gained vs. Lost · weekly cadence</CardMeta>
                </div>
                <Legend>
                  <LegendItem>
                    <LegendDot $color="var(--olive)" />
                    Gained
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $color="var(--crimson)" />
                    Lost
                  </LegendItem>
                </Legend>
              </CardHeader>
              <ChartArea>
                <svg
                  viewBox="0 0 600 200"
                  preserveAspectRatio="none"
                  style={{ width: '100%', height: 180 }}
                >
                  <g stroke="#ede7db" strokeWidth="1">
                    <line x1="0" y1="30" x2="600" y2="30" />
                    <line x1="0" y1="80" x2="600" y2="80" />
                    <line x1="0" y1="130" x2="600" y2="130" />
                  </g>
                  {/* Gained line */}
                  <path
                    d="M40,110 L100,100 L160,90 L220,80 L280,70 L340,60 L400,50 L460,40 L520,34 L570,28"
                    stroke="#4a6b3a"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* Lost line */}
                  <path
                    d="M40,130 L100,128 L160,130 L220,124 L280,118 L340,120 L400,112 L460,104 L520,100 L570,92"
                    stroke="#9c2a1a"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="5 4"
                  />
                  <circle cx="570" cy="28" r="4" fill="#4a6b3a" />
                  <circle cx="570" cy="92" r="4" fill="#9c2a1a" />
                </svg>
              </ChartArea>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <CardTitle>{selected.name} — CPR Evolution</CardTitle>
                  <CardMeta>Consumption Penetration Rate · 12 months</CardMeta>
                </div>
              </CardHeader>
              <CprGrid>
                <CprTile>
                  <CprTileLabel>Current CPR</CprTileLabel>
                  <CprTileValue>
                    21.4<CprUnit>%</CprUnit>
                  </CprTileValue>
                  <CprTrend $up>↑ +3.6pt YoY</CprTrend>
                </CprTile>
                <CprTile>
                  <CprTileLabel>Country Rank</CprTileLabel>
                  <CprTileValue>
                    #2<CprUnit>/ 8</CprUnit>
                  </CprTileValue>
                  <CprTrend $up>↑ +1 place</CprTrend>
                </CprTile>
                <CprTile>
                  <CprTileLabel>vs Global avg</CprTileLabel>
                  <CprTileValue>+3pt</CprTileValue>
                  <CprTrend $up>Above panel avg</CprTrend>
                </CprTile>
              </CprGrid>
              <ChartArea>
                <svg
                  viewBox="0 0 520 160"
                  preserveAspectRatio="none"
                  style={{ width: '100%', height: 140 }}
                >
                  <defs>
                    <linearGradient
                      id="cprAreaDetail"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#c84b1f"
                        stopOpacity={0.18}
                      />
                      <stop offset="100%" stopColor="#c84b1f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <g stroke="#ede7db" strokeWidth="1">
                    <line x1="0" y1="20" x2="520" y2="20" />
                    <line x1="0" y1="60" x2="520" y2="60" />
                    <line x1="0" y1="100" x2="520" y2="100" />
                  </g>
                  <path
                    d="M30,95 L80,88 L130,90 L180,78 L230,68 L280,58 L330,48 L380,40 L430,32 L480,24 L510,18 L510,130 L30,130 Z"
                    fill="url(#cprAreaDetail)"
                  />
                  <path
                    d="M30,95 L80,88 L130,90 L180,78 L230,68 L280,58 L330,48 L380,40 L430,32 L480,24 L510,18"
                    stroke="#c84b1f"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="510" cy="18" r="4" fill="#c84b1f" />
                  <circle cx="510" cy="18" r="8" fill="#c84b1f" opacity={0.2} />
                </svg>
              </ChartArea>
            </Card>
          </SplitRow>
        </>
      )}
    </Page>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

const Page = styled.div`
  padding: 24px 0;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  color: var(--ink-faint);
  font-size: 14px;
`;

const ExportIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="14"
    height="14"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// ─── Table ───────────────────────────────────────────────────────────────────

const TableSection = styled.section`
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 24px;
  background: var(--white);
`;

const TableHeader = styled.div`
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
`;

const CardTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 16px;
  font-weight: 500;
  color: var(--ink);
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  margin-top: 2px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    padding: 10px 14px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid var(--line-soft, #ede7db);
    white-space: nowrap;
  }
`;

const ColSub = styled.span`
  display: block;
  font-size: 9px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-faint);
  opacity: 0.7;
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: var(--paper-warm, #f1ebe1);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const MarketCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MarketFlag = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const MarketName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: var(--ink);
`;

const MarketSub = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  font-weight: 400;
  margin-top: 1px;
`;

const CellPos = styled.td<{ $strong?: boolean }>`
  color: var(--olive);
  ${({ $strong }) =>
    $strong &&
    css`
      font-weight: 600;
    `}
`;

const CellNeg = styled.td<{ $strong?: boolean }>`
  color: var(--crimson);
  ${({ $strong }) =>
    $strong &&
    css`
      font-weight: 600;
    `}
`;

const CellNeutral = styled.td`
  color: var(--ink-faint);
`;

const CellBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CellBar = styled.div`
  width: 60px;
  height: 6px;
  background: var(--line-soft, #ede7db);
  border-radius: 3px;
  overflow: hidden;
`;

const CellBarInner = styled.div<{ $width: number; $positive: boolean }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $positive }) =>
    $positive ? 'var(--olive)' : 'var(--crimson)'};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ChevCell = styled.td`
  color: var(--ink-faint);
  font-size: 18px;
  font-weight: 300;
`;

// ─── Charts ──────────────────────────────────────────────────────────────────

const SplitRow = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 24px;

  ${media.md} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--white);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
  gap: 12px;
  flex-wrap: wrap;
`;

const Legend = styled.div`
  display: flex;
  gap: 14px;
  font-size: 11px;
  color: var(--ink-faint);
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const ChartArea = styled.div`
  padding: 16px 20px;
`;

// ─── Bar chart ───────────────────────────────────────────────────────────────

const BarChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 180px;
  gap: 4px;
`;

const BarGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  position: relative;
  flex: 1;
`;

const Bar = styled.div<{ $height: number; $color: string }>`
  width: 12px;
  height: ${({ $height }) => $height}%;
  background: ${({ $color }) => $color};
  border-radius: 2px 2px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;

  ${media.md} {
    width: 16px;
  }
`;

const BarLabel = styled.div`
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  font-family: var(--font-mono);
  color: var(--ink-faint);
  text-transform: uppercase;
`;

// ─── CPR Grid ────────────────────────────────────────────────────────────────

const CprGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--line);
  border-bottom: 1px solid var(--line);
`;

const CprTile = styled.div`
  background: var(--white);
  padding: 14px 16px;
`;

const CprTileLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-bottom: 6px;
`;

const CprTileValue = styled.div`
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 500;
  color: var(--ink);
`;

const CprUnit = styled.span`
  font-size: 13px;
  color: var(--ink-faint);
  margin-left: 2px;
`;

const CprTrend = styled.div<{ $up?: boolean }>`
  font-size: 11px;
  margin-top: 4px;
  color: ${({ $up }) => ($up ? 'var(--olive)' : 'var(--ink-faint)')};
  font-weight: 500;
`;

// ─── Detail tab ──────────────────────────────────────────────────────────────

const MarketSelector = styled.section`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const MarketSelectorLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
  padding-right: 4px;
`;

const MarketChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: var(--radius-pill, 100px);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--terracotta)' : 'var(--line)')};
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--white)'};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ink-muted)')};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--ink-faint);
    color: ${({ $active }) => ($active ? '#fff' : 'var(--ink)')};
  }
`;

const DetailHero = styled.section`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--white);
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const DetailFlag = styled.div`
  font-size: 40px;
  line-height: 1;
`;

const DetailInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const DetailName = styled.div`
  font-family: var(--font-serif);
  font-size: 22px;
  font-weight: 500;
  color: var(--ink);
`;

const DetailSub = styled.div`
  font-size: 12px;
  color: var(--ink-muted);
  margin-top: 4px;
`;

const DetailStats = styled.div`
  display: flex;
  gap: 28px;
`;

const DetailStat = styled.div`
  text-align: center;
`;

const DetailStatLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-bottom: 4px;
`;

const DetailStatValue = styled.div<{ $positive?: boolean }>`
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 500;
  color: ${({ $positive }) => ($positive ? 'var(--olive)' : 'var(--ink)')};
`;

const DetailStatUnit = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

const DetailGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 24px;

  ${media.md} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const DetailTile = styled.div`
  background: var(--white);
  padding: 18px 20px;
`;

const DetailTileLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-bottom: 10px;
`;

const DetailTileValue = styled.div<{ $color?: string }>`
  font-family: var(--font-mono);
  font-size: 28px;
  font-weight: 500;
  color: ${({ $color }) => $color || 'var(--ink)'};
  line-height: 1;
`;

const DetailTileFoot = styled.div`
  margin-top: 10px;
  font-size: 11px;
  color: var(--ink-faint);
`;

const DeltaUp = styled.span`
  color: var(--olive);
  font-weight: 500;
`;

const DeltaDown = styled.span`
  color: var(--crimson);
  font-weight: 500;
`;
